import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { BehaviorSubject, combineLatest, EMPTY, Observable, of } from 'rxjs';
import { VariableBenefitPaymentFrequencyEnum } from '../../../../../models/enum/shared/variable-benefit-payment-frequency.enum';
import { catchError, distinctUntilChanged, map, shareReplay, startWith, switchMap, take } from 'rxjs/operators';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { RecurringPaymentAmountTypeEnum } from '../../../../../models/enum/shared/recurring-payment-amount-type.enum';
import { RecurringPaymentInstruction } from '../../../../../models/submissions/recurring-payment-instruction';
import * as moment from 'moment';
import { DropDownItem } from '../../../../../models/shared/stylesheet/drop-down-item';
import { recurringPaymentStartDateValidator } from '../utils/vb-payment-change-recurring-payment-start-date.validator';
import { VariableBenefitAccountDetails } from '../../../../../models/transactions/variable-benefit-account-details';
import { CustomError } from '../../../../../models/shared/custom-error';
import { ToastService } from '../../../../../services/toast-service';
import { Subscribable } from '../../../../../models/base/subscribable';
import { SystemBanner } from '../../../../../models/shared/system-banner';
import { VbPaymentChangeRecurringPaymentFormGroup } from '../../../../../models/shared/forms/vb-payment-change-recurring-payment-form-group';
import { VbPaymentChangeFormFactory } from '../utils/vb-payment-change-form-factory';
import {
  payingInstructionOrNull,
  VbPaymentChangePaymentsStepUtils
} from '../utils/vb-payment-change-payments-step.utils';
import { RecurringPaymentInstructionChangeRequest } from '../../../../../models/users/requests/submissionRequests/create-variable-benefit-payment-change-request';
import { DateUtils } from '../../../../../utils/date-utils';

interface MinSchedule {
  start: moment.Moment;
  end: moment.Moment | null;
  isMonthly: boolean;
}

export interface VbPaymentChangeEditRecurringPaymentModalData {
  memberVbAccountDetails$: Observable<VariableBenefitAccountDetails>;
  allowableChangeDate$: Observable<string | null>;
  monthlyRecurringPaymentForm$: Observable<VbPaymentChangeRecurringPaymentFormGroup | null>;
  annualRecurringPaymentForm$: Observable<VbPaymentChangeRecurringPaymentFormGroup | null>;
  estimateMinimumPaymentAmount: (change: RecurringPaymentInstructionChangeRequest) => Observable<number | null>;
  onSaveMonthly: (form: VbPaymentChangeRecurringPaymentFormGroup) => void;
  onSaveAnnual: (form: VbPaymentChangeRecurringPaymentFormGroup) => void;
}

@Component({
  selector: 'app-vb-payment-change-edit-recurring-payment-modal',
  templateUrl: './vb-payment-change-edit-recurring-payment-modal.component.html',
  styleUrls: ['./vb-payment-change-edit-recurring-payment-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VbPaymentChangeEditRecurringPaymentModalComponent extends Subscribable implements OnInit {
  private ngbActiveModal = inject(NgbActiveModal);
  private toastService = inject(ToastService);

  private _isLoading = new BehaviorSubject(false);
  public isLoading$ = this._isLoading.asObservable();

  public formSubmitted = false;

  private _paymentFrequency = new BehaviorSubject<VariableBenefitPaymentFrequencyEnum | null>(null);
  public paymentFrequency$ = this._paymentFrequency.exists();
  public setPaymentFrequency = (paymentFrequency: VariableBenefitPaymentFrequencyEnum) =>
    this._paymentFrequency.next(paymentFrequency);

  private _activeInstruction = new BehaviorSubject<RecurringPaymentInstruction | null>(null);
  public setActiveInstruction = (instruction: RecurringPaymentInstruction | null) =>
    this._activeInstruction.next(payingInstructionOrNull(instruction));

  private _minimumPaymentAmount = new BehaviorSubject<number | null>(null);
  public minimumPaymentAmount$ = this._minimumPaymentAmount.exists();

  private _data!: VbPaymentChangeEditRecurringPaymentModalData;

  private _memberVbAccountDetails$ = new BehaviorSubject<VariableBenefitAccountDetails | null>(null);
  public memberVbAccountDetails$ = this._memberVbAccountDetails$.exists();

  private _allowableChangeDate$ = new BehaviorSubject<string | null>(null);
  public allowableChangeDate$ = this._allowableChangeDate$.exists();

  private _monthlyRecurringPaymentForm$ = new BehaviorSubject<VbPaymentChangeRecurringPaymentFormGroup | null>(null);

  private _annualRecurringPaymentForm$ = new BehaviorSubject<VbPaymentChangeRecurringPaymentFormGroup | null>(null);

  public effectiveDate$ = this.memberVbAccountDetails$.pipe(map(details => details.effectiveDate));

  public isEditingScheduled$ = combineLatest([
    this.paymentFrequency$,
    this._monthlyRecurringPaymentForm$,
    this._annualRecurringPaymentForm$
  ]).pipe(
    map(([paymentFrequency, monthlyForm, annualForm]) => {
      const targetForm = paymentFrequency === VariableBenefitPaymentFrequencyEnum.Monthly ? monthlyForm : annualForm;
      return !!targetForm;
    })
  );

  public annualPaymentHasOccurred$ = combineLatest([this.paymentFrequency$, this._activeInstruction]).pipe(
    map(([paymentFrequency, activeInstruction]) => {
      if (paymentFrequency !== VariableBenefitPaymentFrequencyEnum.Annually) {
        return false;
      }
      return VbPaymentChangePaymentsStepUtils.annualPaymentHasOccurredThisYear(activeInstruction);
    })
  );

  public form$ = combineLatest([
    this.paymentFrequency$,
    this.allowableChangeDate$,
    this._monthlyRecurringPaymentForm$,
    this._annualRecurringPaymentForm$,
    this._activeInstruction
  ]).pipe(
    map(([paymentFrequency, allowableChangeDate, monthlyForm, annualForm, activeInstruction]) => {
      const workingForm = VbPaymentChangeFormFactory.buildRecurringPaymentFormGroup(paymentFrequency);
      const targetForm = paymentFrequency === VariableBenefitPaymentFrequencyEnum.Monthly ? monthlyForm : annualForm;

      if (targetForm) {
        workingForm.patchValue(targetForm.getRawValue());
        if (targetForm.dirty) workingForm.markAsDirty();
      } else if (activeInstruction) {
        workingForm.patchValue({
          recurringPaymentAmountType: activeInstruction.useMinimumAmount
            ? RecurringPaymentAmountTypeEnum.Minimum
            : RecurringPaymentAmountTypeEnum.Custom,
          paymentAmount: activeInstruction.paymentAmount
        });
      }
      if (allowableChangeDate) {
        workingForm.addValidators(recurringPaymentStartDateValidator(allowableChangeDate));
        workingForm.updateValueAndValidity();
      }
      return workingForm;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public paymentAmountTypeValue$ = this.form$.pipe(
    switchMap(fg =>
      fg.controls.recurringPaymentAmountType.valueChanges.pipe(startWith(fg.controls.recurringPaymentAmountType.value))
    )
  );

  private currentFormStartDate$ = this.form$.pipe(
    switchMap(form =>
      combineLatest([
        form.controls.paymentStartMonth.valueChanges.pipe(startWith(form.controls.paymentStartMonth.value)),
        form.controls.paymentStartYear.valueChanges.pipe(startWith(form.controls.paymentStartYear.value))
      ])
    ),
    map(([month, year]) => (month && year ? moment({ year, month: month - 1 }).startOf('month') : null))
  );

  private minimumPaymentStartDate$ = combineLatest([this.form$, this.allowableChangeDate$]).pipe(
    switchMap(([form, allowableChangeDate]) =>
      form.valueChanges.pipe(
        startWith(null),
        map(() => {
          const { paymentStartMonth, paymentStartYear } = form.getRawValue();
          const hasDateError = form.hasError('paymentStartPastDate') || form.hasError('paymentStartCutoffPassed');
          if (!paymentStartMonth || !paymentStartYear || hasDateError) {
            const allowable = moment(allowableChangeDate).startOf('day');
            const earliestStart = allowable.date() === 1 ? allowable : allowable.add(1, 'month').startOf('month');
            return earliestStart.format('YYYY-MM-DD');
          }
          return VbPaymentChangePaymentsStepUtils.firstOfMonth(paymentStartYear, paymentStartMonth);
        })
      )
    ),
    distinctUntilChanged()
  );

  public minimumAlreadyInUse$ = combineLatest([
    this.paymentFrequency$,
    this.memberVbAccountDetails$,
    this._monthlyRecurringPaymentForm$,
    this._annualRecurringPaymentForm$,
    this.currentFormStartDate$
  ]).pipe(
    switchMap(([editingFreq, details, monthlyForm, annualForm, currentStart]) => {
      const editingMonthly = editingFreq === VariableBenefitPaymentFrequencyEnum.Monthly;
      const otherIsMonthly = !editingMonthly;
      const otherPendingForm = editingMonthly ? annualForm : monthlyForm;
      const otherActive = editingMonthly
        ? details.activeAnnualRecurringPaymentInstruction
        : details.activeMonthlyRecurringPaymentInstruction;
      const otherScheduled = editingMonthly
        ? details.scheduledAnnualRecurringPaymentInstruction
        : details.scheduledMonthlyRecurringPaymentInstruction;

      return this.otherSideMinSchedules$(otherIsMonthly, otherPendingForm, otherActive, otherScheduled).pipe(
        map(schedules => schedules.some(s => this.minSchedulesConflict(currentStart, editingMonthly, s)))
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public banner: SystemBanner = {
    bannerType: 'warning',
    text: $localize`The use of a minimum payment can only be used for one type of recurring payment at a time.`,
    layout: 'contained'
  };

  public modalTitle$ = this.paymentFrequency$.pipe(
    map(freq => {
      return freq === VariableBenefitPaymentFrequencyEnum.Monthly
        ? $localize`Monthly Payment`
        : $localize`Annual Payment`;
    })
  );
  public radioButtonLabels$ = combineLatest([this.paymentFrequency$, this.minimumPaymentAmount$]).pipe(
    map(([freq, minAmount]) => {
      const freqString = freq === VariableBenefitPaymentFrequencyEnum.Monthly ? $localize`monthly` : $localize`annual`;
      const freqUnit = freq === VariableBenefitPaymentFrequencyEnum.Monthly ? $localize`Month` : $localize`Year`;

      return {
        [RecurringPaymentAmountTypeEnum.Minimum]: $localize`Use the ${freqString} minimum of $${minAmount.toFixed(
          2
        )}/${freqUnit}`
      };
    })
  );

  public customErrorMessages = {
    paymentStartMonth: {
      required: $localize`Payment Start Month is required`,
      paymentStartPastDate: $localize`Select a future date`,
      paymentStartCutoffPassed: $localize`Select a later date`
    },
    paymentStartYear: {
      required: $localize`Payment Year is required`
    },
    paymentAmount: {
      required: $localize`Payment Amount is required`,
      min: $localize`Payment Amount must be $0 or greater`
    }
  };

  public monthDropdowns = Array.from(
    { length: 12 },
    (_, i) => new DropDownItem(moment().month(i).format('MMMM'), i + 1)
  );

  public yearDropdowns$ = this.annualPaymentHasOccurred$.pipe(
    startWith(false),
    map(hasOccurred => {
      const currentYear = DateUtils.currentYearInCST();
      if (hasOccurred) {
        return [new DropDownItem((currentYear + 1).toString(), currentYear + 1)];
      }
      return [
        new DropDownItem(currentYear.toString(), currentYear),
        new DropDownItem((currentYear + 1).toString(), currentYear + 1)
      ];
    })
  );

  public ngOnInit() {
    this.initMinimumPaymentAmountListener();
    this.initAutoSelectYear();
    this.listenToPaymentAmountType();
    this.listenToMinimumAlreadyInUse();
  }

  private listenToMinimumAlreadyInUse(): void {
    combineLatest([this.minimumAlreadyInUse$, this.form$]).subscribeWhileAlive({
      owner: this,
      next: ([minimumInUse, form]) => {
        if (minimumInUse && form.controls.recurringPaymentAmountType.value === RecurringPaymentAmountTypeEnum.Minimum) {
          form.controls.recurringPaymentAmountType.setValue(RecurringPaymentAmountTypeEnum.Custom);
        }
      }
    });
  }

  public setData(data: VbPaymentChangeEditRecurringPaymentModalData): void {
    this._data = data;
    this._memberVbAccountDetails$.next(null);
    data.memberVbAccountDetails$.pipe(take(1)).subscribe(d => this._memberVbAccountDetails$.next(d));
    data.allowableChangeDate$.pipe(take(1)).subscribe(d => this._allowableChangeDate$.next(d));
    data.monthlyRecurringPaymentForm$.pipe(take(1)).subscribe(f => this._monthlyRecurringPaymentForm$.next(f));
    data.annualRecurringPaymentForm$.pipe(take(1)).subscribe(f => this._annualRecurringPaymentForm$.next(f));
  }

  private initMinimumPaymentAmountListener(): void {
    combineLatest([this.minimumPaymentStartDate$, this.minimumAlreadyInUse$])
      .pipe(
        switchMap(([paymentStartDate, minimumInUse]) => {
          if (minimumInUse) {
            this._isLoading.next(false);
            return EMPTY;
          }
          this._isLoading.next(true);
          const change = new RecurringPaymentInstructionChangeRequest(paymentStartDate, 0, true);
          return this._data.estimateMinimumPaymentAmount(change).pipe(
            catchError((err: CustomError) => {
              this._isLoading.next(false);
              this.toastService.publishError(err);
              return EMPTY;
            })
          );
        })
      )
      .subscribeWhileAlive({
        owner: this,
        next: amount => {
          this._minimumPaymentAmount.next(amount);
          this._isLoading.next(false);
        }
      });
  }

  private initAutoSelectYear(): void {
    combineLatest([this.annualPaymentHasOccurred$, this.form$])
      .pipe(take(1))
      .subscribe(([hasOccurred, form]) => {
        if (hasOccurred) {
          const nextYear = DateUtils.currentYearInCST() + 1;
          form.controls.paymentStartYear.setValue(nextYear);
          form.controls.paymentStartYear.disable();
        }
      });
  }

  private listenToPaymentAmountType(): void {
    combineLatest([this.form$, this.minimumPaymentAmount$, this.paymentAmountTypeValue$]).subscribeWhileAlive({
      owner: this,
      next: ([form, minAmount, paymentAmountType]) => {
        if (paymentAmountType === RecurringPaymentAmountTypeEnum.Custom) {
          form.controls.paymentAmount.enable();
        }
        if (paymentAmountType === RecurringPaymentAmountTypeEnum.Minimum) {
          form.controls.paymentAmount.setValue(minAmount);
          form.controls.paymentAmount.disable();
        }
      }
    });
  }

  // Returns the other frequency's known minimum-using payment schedules
  private otherSideMinSchedules$(
    otherIsMonthly: boolean,
    pendingForm: VbPaymentChangeRecurringPaymentFormGroup | null,
    active: RecurringPaymentInstruction | null,
    scheduled: RecurringPaymentInstruction | null
  ): Observable<MinSchedule[]> {
    if (pendingForm) {
      return this.pendingFormMinSchedule$(pendingForm, otherIsMonthly).pipe(map(s => (s ? [s] : [])));
    }
    return of(
      [active, scheduled]
        .map(i => this.instructionMinSchedule(i, otherIsMonthly))
        .filter((s): s is MinSchedule => s !== null)
    );
  }

  private pendingFormMinSchedule$(
    form: VbPaymentChangeRecurringPaymentFormGroup,
    isMonthly: boolean
  ): Observable<MinSchedule | null> {
    const amountType$ = form.controls.recurringPaymentAmountType.valueChanges.pipe(
      startWith(form.controls.recurringPaymentAmountType.value)
    );
    const start$ = combineLatest([
      form.controls.paymentStartMonth.valueChanges.pipe(startWith(form.controls.paymentStartMonth.value)),
      form.controls.paymentStartYear.valueChanges.pipe(startWith(form.controls.paymentStartYear.value))
    ]).pipe(map(([month, year]) => (month && year ? moment({ year, month: month - 1 }).startOf('month') : null)));

    return combineLatest([amountType$, start$]).pipe(
      map(([amountType, start]) => {
        if (amountType !== RecurringPaymentAmountTypeEnum.Minimum || !start) return null;
        return { start, end: null, isMonthly };
      })
    );
  }

  private instructionMinSchedule(
    instruction: RecurringPaymentInstruction | null,
    isMonthly: boolean
  ): MinSchedule | null {
    if (!instruction?.useMinimumAmount) return null;
    const start = moment.utc(instruction.paymentStartDate).startOf('month');
    const end = instruction.endDate ? moment.utc(instruction.endDate).startOf('month') : null;
    if (isMonthly) return { start, end, isMonthly };
    // An annual instruction fires once a year in its start month. Once this year's payment has
    // happened there is nothing left to conflict with; otherwise its next firing is this year's.
    if (VbPaymentChangePaymentsStepUtils.annualPaymentHasOccurredThisYear(instruction)) return null;
    const currentYear = DateUtils.currentYearInCST();
    const nextFiring = start.year() < currentYear ? start.clone().year(currentYear) : start;
    return { start: nextFiring, end, isMonthly };
  }

  private minSchedulesConflict(newStart: moment.Moment | null, newIsMonthly: boolean, other: MinSchedule): boolean {
    if (!newStart) return true;

    if (newIsMonthly && other.isMonthly) {
      const overlapStart = newStart.isAfter(other.start, 'month') ? newStart : other.start;
      if (other.end && overlapStart.isAfter(other.end, 'month')) return false;
      return true;
    }

    // Annual minimums are one-shot — they fire once on their start month (see
    // instructionMinSchedule, which moves an existing annual instruction to its next firing).
    if (!newIsMonthly && !other.isMonthly) {
      return newStart.isSame(other.start, 'month');
    }

    // Mixed: collision iff the annual's single firing falls within the monthly's active range.
    const annualStart = newIsMonthly ? other.start : newStart;
    const monthlyStart = newIsMonthly ? newStart : other.start;
    const monthlyEnd = newIsMonthly ? null : other.end;

    if (annualStart.isBefore(monthlyStart, 'month')) return false;
    if (monthlyEnd && annualStart.isAfter(monthlyEnd, 'month')) return false;
    return true;
  }

  public cancel(): void {
    this.ngbActiveModal.close();
  }

  public savePaymentDetails(): void {
    this.formSubmitted = true;
    combineLatest([this.paymentFrequency$, this.form$])
      .pipe(take(1))
      .subscribe(([frequency, form]) => {
        if (form.invalid) return;

        if (frequency === VariableBenefitPaymentFrequencyEnum.Monthly) {
          this._data.onSaveMonthly(form);
        }

        if (frequency === VariableBenefitPaymentFrequencyEnum.Annually) {
          this._data.onSaveAnnual(form);
        }

        this.ngbActiveModal.close();
      });
  }

  protected readonly RecurringPaymentAmountTypeEnum = RecurringPaymentAmountTypeEnum;
}
